import { allCagesOf, type OverviewCageBuckets } from '@/domain/client';

export type DemoMaskType = 'client' | 'location' | 'cage' | 'fishGroup' | 'stockingCycle';

/** i18n keys for demo display labels – single source of truth */
export const DEMO_LABEL_KEYS: Record<DemoMaskType, string> = {
    client: 'common.demo.client',
    location: 'common.demo.location',
    cage: 'common.demo.cage',
    fishGroup: 'common.demo.fishGroup',
    stockingCycle: 'common.demo.stockingCycle',
} as const;

const DEMO_ID_SEEDS: Record<DemoMaskType, number> = {
    client: 1000,
    location: 2000,
    cage: 3000,
    fishGroup: 4000,
    stockingCycle: 5000,
};

/**
 * Generate a deterministic "random" ID for masking
 * Same real ID will always map to the same masked ID
 * This ensures consistency across frontend and backend
 */
export function getMaskedId(realId: number, type: DemoMaskType): number {
    // Use different seeds for each type to ensure uniqueness
    const seed = DEMO_ID_SEEDS[type];
    // Simple hash function: multiply by prime, add seed, modulo to get range 1000-9999
    const hash = ((realId * 7919 + seed) % 9000) + 1000;
    return hash;
}

/**
 * Returns the display name for a demo-masked entity (e.g. "Cliente 1234").
 * Centralizes format and i18n key so changes are made in one place.
 */
export function getDemoDisplayName(
    id: number,
    type: DemoMaskType,
    t: (key: string) => string
): string {
    return `${t(DEMO_LABEL_KEYS[type])} ${getMaskedId(id, type)}`;
}

/** Minimal shape for entities that can be demo-masked (client with locations) */
export interface ClientLike {
    id: number;
    name: string;
    locations: Array<{ id: number; name: string }>;
}

/** Minimal shape for entities that can be demo-masked (location with optional cages) */
export interface LocationLike {
    id: number;
    name: string;
    cages?: Array<{ id: number; name: string }>;
}

/**
 * Returns a copy of the client with name and each location's name replaced by demo display names.
 */
export function applyDemoMaskToClient<T extends ClientLike>(
    client: T,
    t: (key: string) => string
): T {
    return {
        ...client,
        name: getDemoDisplayName(client.id, 'client', t),
        locations: client.locations.map((loc) => ({
            ...loc,
            name: getDemoDisplayName(loc.id, 'location', t),
        })),
    };
}

/**
 * Returns a copy of the location with name and each cage's name replaced by demo display names.
 */
export function applyDemoMaskToLocation<T extends LocationLike>(
    location: T,
    t: (key: string) => string
): T {
    return {
        ...location,
        name: getDemoDisplayName(location.id, 'location', t),
        cages:
            location.cages?.map((cage) => ({
                ...cage,
                name: getDemoDisplayName(cage.id, 'cage', t),
            })) ?? [],
    };
}

/** Shape used by the Client Overview domain types — separate id/name fields per entity. */
export interface OverviewCageLike {
    cageId: number;
    cageName: string;
    /**
     * Optional so callers that never carry a cohort still satisfy the shape. When
     * present the name is masked too — a fish group name is customer data, often
     * the batch or supplier reference, and leaving it through would defeat the
     * masking of the cage it sits next to.
     */
    activeFishGroup?: { name: string } | null;
}

/**
 * A departed cohort row. Both names are customer data and both get masked — the
 * cage name for the same reason the cage rows do, the group name because it is
 * usually the batch or supplier reference.
 */
export interface ClosedFishGroupLike {
    cageId: number;
    cageName: string;
    fishGroupName: string;
}

export interface OverviewLocationLike extends OverviewCageBuckets<OverviewCageLike> {
    locationId: number;
    locationName: string;
    cages: OverviewCageLike[];
    /** Optional so callers without cohort rows still satisfy the shape. */
    closedFishGroups?: ClosedFishGroupLike[];
}

function maskOverviewCage<C extends OverviewCageLike>(cage: C, t: (key: string) => string): C {
    return {
        ...cage,
        cageName: getDemoDisplayName(cage.cageId, 'cage', t),
        // Only rewritten when the key is there, so a cage without the field keeps
        // its shape and "not requested" does not turn into "no cohort".
        ...('activeFishGroup' in cage && cage.activeFishGroup
            ? {
                  activeFishGroup: {
                      ...cage.activeFishGroup,
                      name: getDemoDisplayName(cage.cageId, 'cage', t),
                  },
              }
            : {}),
    };
}

/**
 * Returns a copy of an Overview location with masked name and masked cage names across the
 * status sub-arrays.
 */
export function applyDemoMaskToOverviewLocation<T extends OverviewLocationLike>(
    location: T,
    t: (key: string) => string
): T {
    const mask = (cages: OverviewCageLike[]) => cages.map((c) => maskOverviewCage(c, t));
    const buckets = {
        activeCages: mask(location.activeCages),
        inactiveCages: mask(location.inactiveCages),
        emptyCages: mask(location.emptyCages),
        historicalCages: mask(location.historicalCages),
    };
    return {
        ...location,
        locationName: getDemoDisplayName(location.locationId, 'location', t),
        ...buckets,
        cages: allCagesOf(buckets),
        // The spread above would have carried these through unmasked, which is the
        // kind of leak that only shows up in a demo in front of a customer.
        ...(location.closedFishGroups
            ? {
                  closedFishGroups: location.closedFishGroups.map((stay) => ({
                      ...stay,
                      cageName: getDemoDisplayName(stay.cageId, 'cage', t),
                      fishGroupName: getDemoDisplayName(stay.cageId, 'cage', t),
                  })),
              }
            : {}),
    };
}
